import type { ReactElement } from 'react';
import {
  Children,
  cloneElement,
  isValidElement,
  useEffect,
  useRef,
  useState,
} from 'react';

import classNames from 'classnames';
import { useSelect } from 'downshift';

import styles from './StyledSelect.module.css';

import { useElementState } from '../../hooks/useElementState';
import { Caret } from '../../icons/Caret';
import { Chevron } from '../../icons/Chevron';
import { ChevronAccordion } from '../../icons/ChevronAccordion';
import { FieldLabel } from '../FieldLabel';
import { Text } from '../Text';
import { InlineStyledSelect } from './InlineStyledSelect';
import type { StyledOptionProps } from './components';
import { StyledOptionList } from './components';
import type { MultiProps } from './hooks';
import { useMultiSelect } from './hooks';
import { isStyledOption, stringify } from './utils/option';

export type StyledSelectIcon = 'caret' | 'chevron';

type StyledSelectBaseProps = MultiProps & {
  /** Label and placeholder of select */
  label: string;
  /** Display invalid state */
  invalid?: boolean;
  onChange: (event?: { value?: string | number; checked: boolean }) => void;
  children: ReactElement<StyledOptionProps>[];
  className?: string;
  /** Initial value of the select to display */
  value: string | number | undefined;
  highlighted?: boolean;
  /** Dropdown icon style */
  icon?: StyledSelectIcon;
  /** Apply bold font weight to selection */
  bold?: boolean;
};

/**
 * Renders the trigger as inline text — the selected value styled like a link,
 * then the chevron. The `label` prop is visually hidden and used only as the
 * accessible name; surround this component with your own text context.
 */
type InlineVariantProps = {
  inline: true;
  hideLabel?: never;
  bold?: never;
  icon?: never;
  disabled?: never;
};

type BoxedVariantProps = {
  inline?: false;
  /** Hide FieldLabel but keep it in the accessibility tree */
  hideLabel?: boolean;
};

export type StyledSelectProps = StyledSelectBaseProps &
  (InlineVariantProps | BoxedVariantProps);

/**
 * Render a select field styled as the other form elements. Implements
 * https://downshift-js.com. Use {@link SearchableSelect} for a styled select
 * with search functionality.
 *
 * @see https://corgi-x.tfd.engineering/components/styledselect
 */
export function StyledSelect(props: StyledSelectProps) {
  const inline = props.inline === true;
  const hideLabel = props.inline === true ? false : (props.hideLabel ?? false);
  const {
    label,
    children,
    className,
    value,
    highlighted,
    disabled,
    invalid,
    icon = 'caret',
    bold = false,
    ...restProps
  } = props as StyledSelectBaseProps & {
    inline?: boolean;
    hideLabel?: boolean;
    disabled?: boolean;
  };

  const positionRef = useRef(null);
  const [state, handlers] = useElementState({ highlighted, disabled, invalid });

  // If component does not receive value as `null` or `undefined`, then we want
  // to prevent handleChange to be triggered on render
  const [preventHandleChangeOnRender, setPreventHandleChangeOnRender] =
    // This eqeqeq lint error was disabled when the eqeqeq rule was introduced.
    // Please update the code to use !== if it's safe to do so.
    // eslint-disable-next-line eqeqeq
    useState(value != undefined);

  // This state keeps track if `useSelect`'s `reset` function has been called to
  // only call it once, once `value` becomes `undefined`
  const [hasReset, setHasReset] = useState(false);

  const options = Children.toArray(children) as typeof children;

  const valueItem = options.find(option => {
    if (isStyledOption(option)) {
      return option.props.value === value;
    } else {
      return option === value;
    }
  });

  const { getIsSelected, hasValue, handleChange } = useMultiSelect({
    value,
    ...restProps,
  });

  const {
    isOpen,
    highlightedIndex,
    selectedItem,
    selectItem,
    reset,
    ...select
  } = useSelect({
    items: options,
    initialSelectedItem: valueItem,
    itemToString: stringify,
    onSelectedItemChange: change => {
      // if there has been a change, then set `hasReset` to `false` for `reset`
      // to be called again
      if (hasReset) {
        setHasReset(false);
      }
      // prevents onChange to be triggered when initially rendered
      if (preventHandleChangeOnRender) {
        setPreventHandleChangeOnRender(false);
        return;
      }
      handleChange(change);
    },
  });

  const [buttonLabel, setButtonLabel] = useState(
    isStyledOption(selectedItem)
      ? selectedItem.props.label
      : stringify(selectedItem)
  );

  /**
   * This effect is needed to make sure the button label is always up to date.
   * For some reason likely due to Downshift's state management, the label on
   * selectedItem does not update with the option list automatically. Should we
   * decide to update this component to handle the selected item state instead
   * of within Downshift (which Downshift supports) this hacky solution
   * shouldn't be needed.
   */
  useEffect(() => {
    const selectedOptionInList = options.find(option => {
      if (isStyledOption(option) && isStyledOption(selectedItem)) {
        return option.props.value === selectedItem.props.value;
      }
      return false;
    });

    // If a selected item can't be found that means the options were updated
    // and the new list does not include the selected item. In this case we
    // should reset it to the initial selected item if it exists.
    if (!selectedOptionInList) {
      if (valueItem) {
        selectItem(valueItem);
      }
      return;
    }

    setButtonLabel(selectedOptionInList.props.label);
  }, [options, selectedItem, valueItem, selectItem]);

  useEffect(() => {
    /*
     * Since `useSelect` persists `selectedItem`, we need to call `reset` to set
     * `selectedItem` back to `undefined` in the case of dynamic selects. To do
     * this, we need to:
     *   1. keep track if reset has been called before with `hasReset` to not
     *      call multiple times since `selectedItem` does not update immediately
     *   2. set button label to `undefined` since the value will still be
     *      displayed within the body of the button element
     */
    if (typeof value === 'undefined' && !hasReset) {
      setHasReset(true);
      reset();
      setButtonLabel(undefined);
    }
  }, [value, hasReset, setHasReset, reset]);

  // The two effects below allow external state changes.
  const [shouldUpdateSelectedItem, setShouldUpdateSelectedItem] =
    useState(false);

  useEffect(() => {
    setShouldUpdateSelectedItem(true);
  }, [value]);

  useEffect(() => {
    if (shouldUpdateSelectedItem) {
      setShouldUpdateSelectedItem(false);

      if (valueItem) {
        selectItem(valueItem);
      }
    }
  }, [shouldUpdateSelectedItem, valueItem, selectItem]);

  useEffect(() => {
    if (
      !shouldUpdateSelectedItem &&
      valueItem &&
      selectedItem?.props.value !== valueItem.props.value
    ) {
      setShouldUpdateSelectedItem(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  const optionList = (
    <StyledOptionList
      {...select.getMenuProps(handlers)}
      isOpen={isOpen}
      positionElement={positionRef.current}
    >
      {options.map((option, index) => {
        if (!isValidElement(option)) {
          return option;
        }

        return cloneElement(option, {
          hover: index === highlightedIndex,
          selected: getIsSelected(option),
          bold: getIsSelected(option) && bold,
          ...select.getItemProps({ index, item: option }),
        });
      })}
    </StyledOptionList>
  );

  if (inline) {
    return (
      <InlineStyledSelect
        label={label}
        className={className}
        selectedLabel={buttonLabel}
        isOpen={isOpen}
        labelProps={select.getLabelProps()}
        toggleButtonProps={select.getToggleButtonProps({
          ...handlers,
          ref: positionRef,
          id: restProps.id,
          name: restProps.name,
        })}
      >
        {optionList}
      </InlineStyledSelect>
    );
  }

  return (
    <div className={classNames(styles.container, className)}>
      <FieldLabel
        label={label}
        state={state}
        in={
          Boolean(value) ||
          Boolean(state === 'focus' || hasValue || state === 'invalid')
        }
        hideLabel={hideLabel}
        {...select.getLabelProps()}
      >
        <Text asChild color="Kale3">
          <button
            {...select.getToggleButtonProps({
              ...handlers,
              ref: positionRef,
              id: restProps.id,
              name: restProps.name,
            })}
            // Following accessibility best practices, `disabled` attribute is
            // not used and events are disabled programmatically instead.
            {...(disabled ? { onClick: undefined } : {})}
            aria-disabled={disabled}
            className={styles.button}
            type="button"
          >
            <Text
              className={classNames(styles.buttonText, {
                [styles.boldSelectedValue]: bold,
              })}
            >
              {buttonLabel}
            </Text>

            <span className={styles.defaultIcon}>
              {icon === 'chevron' ? (
                <Chevron
                  aria-hidden
                  size={36}
                  fill="kale-3"
                  className={classNames(styles.buttonIcon, styles[icon], {
                    [styles.isOpen]: isOpen,
                  })}
                />
              ) : (
                <Caret
                  aria-hidden
                  size={36}
                  fill="kale-3"
                  className={classNames(styles.buttonIcon, {
                    [styles.isOpen]: isOpen,
                  })}
                />
              )}
            </span>
            <span className={styles.foundationIcon}>
              <ChevronAccordion
                aria-hidden
                orientation={isOpen ? 'up' : 'down'}
                size={24}
                baseColor="Sage700"
                className={styles.buttonIcon}
              />
            </span>
          </button>
        </Text>
      </FieldLabel>
      {optionList}
    </div>
  );
}
